<script>
	import { signedInUser } from '@ctm/auth/store';
	import { navigating, page } from '$app/stores';
	import { isAuthenticated, accessToken } from '$lib/stores/user';
	import { debtorSettings, settingsError, settingsLoaded, hasPermission, userDebtors } from '$lib/stores/settings';

	import Error from '$lib/components/Error/Error.svelte';
	import Auth from '$lib/components/Auth/Auth.svelte';
	import Nav from '$lib/components/Nav/Nav.svelte';
	import InactivityTimeout from '$lib/components/InactivityTimeout/InactivityTimeout.svelte';
	import PageLoader from '$lib/components/PageLoader/PageLoader.svelte';
	import Scout from '$lib/components/Scout/Scout.svelte';
	import Beamer from '$lib/components/Beamer/Beamer.svelte';
	import Notification from '$lib/components/Notification/Notification.svelte';
	import Sidebar from './sidebar/Sidebar.svelte';
	import { Alert, Home } from '@ctm/icons/interface';
	import { Location } from '@ctm/icons/travel';
	import { Individual, TicketCredit } from '@ctm/icons/booking';
	import { Announcements } from './sidebar/Announcements';
	import { companyNewsUnreadCount, updateUnreadCount } from '$lib/stores/companyNews';
	import { onDestroy } from 'svelte';
	import { RiskIcon } from './group-risk/RiskIcon';

	$: authenticated = $accessToken && $isAuthenticated && $signedInUser && $settingsLoaded;

	$: if ($settingsLoaded && hasPermission('portal.companynews') && authenticated) {
		initializePolling();
	} else {
		stopPolling();
		document.removeEventListener('visibilitychange', handleVisibilityChange);
	}

	async function initializePolling() {
		// Only initialize if polling is not already running
		if (pollingInterval !== null) {
			return;
		}

		await updateUnreadCount();
		document.addEventListener('visibilitychange', handleVisibilityChange);

		if (!document.hidden) {
			startPolling();
		}
	}

	const { debtor, impersonationToken } = $page.data;
	let showNotification = false;
	let message = '';

	// Routes that require specific permissions to access
	const routePermissions = {
		'/track': 'portal.travel.tracker',
		'/people': 'portal.default.people',
		'/credits': 'portal.ticket.credit',
		'/group-risk': 'portal.riskgroupreport',
		'/risk-hub': 'portal.default.riskhub',
		'/company-news': 'portal.companynews',
	};

	$: currentRoute = $page?.url.pathname;
	$: requiredPermission = Object.entries(routePermissions).find(([route]) => currentRoute.startsWith(route))?.[1];
	$: hasRequiredPermission = !requiredPermission || ($settingsLoaded && hasPermission(requiredPermission));

	$: if ($settingsLoaded && (debtor || impersonationToken)) showDebtorNotification();

	let pollingInterval = null;
	let lastPollTime = Date.now();
	let isTabVisible = true;

	// Polling interval every 2 minutes to update company news unread count
	const POLL_INTERVAL = 2 * 60 * 1000;

	function showDebtorNotification() {
		showNotification = true;
		if (debtor) {
			const { debtorName } = $userDebtors.find((d) => d.debtorCode === debtor);
			message = `Successfully changed to "${debtorName}"`;
		} else if (impersonationToken) {
			message = `Logged in as  "${$signedInUser.email} - ${$signedInUser.debtor}"`;
		}
	}

	function hideDebtorNotification() {
		showNotification = false;
	}

	$: sidebarExpandable = currentRoute === '/dashboard';

	async function updatePostsCount() {
		await updateUnreadCount();
		lastPollTime = Date.now();
	}

	function startPolling() {
		if (pollingInterval !== null) {
			clearInterval(pollingInterval);
		}

		pollingInterval = setInterval(updatePostsCount, POLL_INTERVAL);
	}

	function stopPolling() {
		if (pollingInterval !== null) {
			clearInterval(pollingInterval);
			pollingInterval = null;
		}
	}

	function handleVisibilityChange() {
		const currentTime = Date.now();
		isTabVisible = !document.hidden;

		if (isTabVisible) {
			const timeSinceLastPoll = currentTime - lastPollTime;

			if (timeSinceLastPoll >= POLL_INTERVAL) {
				updatePostsCount();
			}

			startPolling();
		} else {
			stopPolling();
		}
	}

	onDestroy(() => {
		stopPolling();
		document.removeEventListener('visibilitychange', handleVisibilityChange);
	});

	$: sidebarLinks = $settingsLoaded
		? [
				{ label: 'Home', href: '/dashboard', icon: Home },
				...(hasPermission('portal.companynews') ? [{ label: 'Company News', href: '/company-news', icon: Announcements, count: $companyNewsUnreadCount }] : []),
				...(hasPermission('portal.travel.tracker') ? [{ label: 'Track', href: '/track', icon: Location }] : []),
				...(hasPermission('portal.default.people') ? [{ label: 'People', href: '/people', icon: Individual }] : []),
				...(hasPermission('portal.ticket.credit') ? [{ label: 'Credits', href: '/credits', icon: TicketCredit }] : []),
				...(hasPermission('portal.riskgroupreport') ? [{ label: 'Risk Group Reporting', href: '/group-risk', icon: RiskIcon }] : []),
				...(hasPermission('portal.default.riskhub')
					? [
							{
								label: 'RISK Hub',
								href: '/risk-hub',
								icon: Alert,
								external: true,
							},
						]
					: []),
			]
		: [{ label: 'Home', href: '/dashboard', icon: Home }];
</script>

<Auth />

{#if $settingsError}
	<Error />
{:else if !$settingsLoaded}
	<PageLoader />
{:else if authenticated}
	{#if !hasRequiredPermission}
		<Error statusCode={403} />
	{:else}
		<div class="container">
			{#if !['/sso', '/risk-hub'].includes($page?.url.pathname)}
				<Nav />
			{/if}

			<InactivityTimeout />
			{#if $navigating}
				<PageLoader />
			{/if}

			{#if showNotification}
				<Notification type="info" dismissable={true} displayType="modal" {message} on:close={hideDebtorNotification} />
			{/if}

			{#if hasPermission('portal.scout')}
				<Scout />
			{/if}

			{#if $debtorSettings.beamerEnabled}
				<Beamer />
			{/if}

			<div class="layout-body">
				{#if currentRoute !== '/risk-hub'}
					<Sidebar expandable={sidebarExpandable} links={sidebarLinks} {currentRoute} logo={$debtorSettings.logoUrl} />
				{/if}

				<div class="main-content">
					<slot />
				</div>
			</div>
		</div>
	{/if}
{:else}
	<PageLoader />
{/if}

<style>
	.container {
		display: flex;
		height: 100vh;
		overflow: hidden;
		flex-direction: column;

		/* background-color: var(--red_3); */
	}

	.layout-body {
		display: flex;
		flex-direction: row;
		flex: 1;
		/* overflow: hidden; */
		min-height: 0;
	}

	.main-content {
		flex: 1;
		padding: 24px;
		/* background-color: var(--blue_3); */
		overflow-y: auto;
		position: relative;
	}

	@media print {
		.container {
			height: auto !important;
			overflow: visible !important;
		}
		.main-content {
			overflow: visible !important;
			padding: 0 !important;
		}
		.layout-body {
			overflow: visible !important;
			display: block !important;
		}
	}
</style>
